import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowUpRight, Loader2 } from "lucide-react";
import PageHero from "../components/layout/PageHero";
import Reveal from "../components/Reveal";
import ArticleCard from "../components/blog/ArticleCard";
import { Input } from "../components/ui/input";
import { api, imageSized } from "../api";
import { formatDate, useDocumentTitle } from "../lib/blog";

const PAGE_SIZE = 9;

export default function Blog() {
  useDocumentTitle("Blog | Novocreste International", "Insights on pharmaceutical sourcing, regulatory support and global markets from Novocreste.");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);

  // Debounce the search box
  useEffect(() => {
    const t = setTimeout(() => setSearch(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const fetchPage = useCallback(
    async (skip) => {
      const res = await api.get("/articles", { params: { category, search, limit: PAGE_SIZE, skip } });
      return res.data;
    },
    [category, search]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchPage(0)
      .then((d) => {
        if (cancelled) return;
        setItems(d.items);
        setTotal(d.total);
        if (category === "All" && !search) setCategories(d.categories);
      })
      .catch(() => !cancelled && setError(true))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [fetchPage, category, search]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const d = await fetchPage(items.length);
      setItems((prev) => [...prev, ...d.items]);
    } finally {
      setLoadingMore(false);
    }
  };

  const showFeatured = category === "All" && !search && items.length > 1;
  const featured = showFeatured ? items[0] : null;
  const grid = showFeatured ? items.slice(1) : items;

  return (
    <>
      <PageHero
        eyebrow="Blog"
        title="Insights on global pharmaceutical sourcing"
        subtitle="Guides, regulatory updates and market perspectives from the Novocreste team."
      />

      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          {/* Controls */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
            <div className="relative w-full lg:max-w-md">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search articles..."
                className="pl-10 h-12 rounded-full bg-card"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {["All", ...categories].map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`px-4 h-10 rounded-full text-sm font-medium border transition-colors ${
                    category === c
                      ? "bg-[#00ab96] text-white border-[#2ED9B8]"
                      : "bg-card text-muted-foreground border-border hover:border-[#2ED9B8]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="mt-16 flex justify-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : error ? (
            <p className="mt-16 text-center text-muted-foreground">We couldn't load the articles. Please try again shortly.</p>
          ) : items.length === 0 ? (
            <p className="mt-16 text-center text-muted-foreground">
              {search || category !== "All" ? "No articles match your search." : "New articles are coming soon."}
            </p>
          ) : (
            <>
              {featured && (
                <Reveal className="mt-10">
                  <Link
                    to={`/blog/${featured.slug}`}
                    className="group grid lg:grid-cols-2 bg-card rounded-[24px] border border-white/10 overflow-hidden hover:border-[#2ED9B8]/25 transition-colors"
                  >
                    <div className="aspect-[16/10] lg:aspect-auto lg:min-h-[340px] overflow-hidden bg-[#0f2236]">
                      {featured.cover && (
                        <img src={imageSized(featured.cover, 1200)} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      )}
                    </div>
                    <div className="p-8 lg:p-12 flex flex-col justify-center">
                      <span className="inline-block w-fit text-[11px] uppercase tracking-[0.14em] text-[#2ED9B8] bg-[#0f2236] px-2.5 py-1 rounded-full">
                        Featured · {featured.category}
                      </span>
                      <h2 className="mt-5 font-display text-[28px] lg:text-[36px] leading-[1.15] text-foreground">{featured.title}</h2>
                      <p className="mt-4 text-muted-foreground leading-relaxed">{featured.excerpt}</p>
                      <p className="mt-6 text-xs text-muted-foreground">{formatDate(featured.published_at)} · {featured.read_minutes} min read</p>
                      <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-[#2ED9B8] group-hover:gap-2 transition-all">
                        Read article <ArrowUpRight className="h-4 w-4" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              )}

              <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
                {grid.map((a, i) => (
                  <Reveal key={a.slug} delay={(i % 3) * 60}>
                    <ArticleCard article={a} />
                  </Reveal>
                ))}
              </div>

              {items.length < total && (
                <div className="mt-10 flex justify-center">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="inline-flex items-center gap-2 bg-card hover:bg-[#0f2236] text-foreground border border-border rounded-full px-7 h-12 font-medium transition-colors disabled:opacity-60"
                  >
                    {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />} Load more articles
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
}
